import { Stack } from '@chakra-ui/react';
import React from 'react';

import { Badge, Typography } from 'shared';

import { WidgetCardWrapper } from 'features/chat/components/ChatMessages/ChatMessage/Widgets/CardWrapper';
import { Widget } from 'features/chat/config/types/Message';

import {
  getStatusBadgeTokens,
  getWidgetMeta,
  getWidgetPayload,
  getWidgetSubtitle,
  getWidgetTitle,
  getWidgetType,
  resolveStatusText,
  toStartCase,
  getFirstString,
  WidgetMeta,
} from './widgetMeta';

type GenericWidgetProps = {
  widget: Widget;
  meta?: WidgetMeta;
};

export const GenericWidget = ({ widget, meta }: GenericWidgetProps) => {
  const computedMeta = meta ?? getWidgetMeta(widget);
  const widgetType = getWidgetType(widget);
  const title =
    getWidgetTitle(widget, computedMeta) ||
    toStartCase(computedMeta.statusLabel) ||
    toStartCase(widgetType ?? '');
  const subtitle = getWidgetSubtitle(widget, computedMeta);
  const description = buildDescription(widget, subtitle);
  const statusTokens = getStatusBadgeTokens(computedMeta.statusVariant);
  const statusText = resolveStatusText(widget, computedMeta);

  return (
    <WidgetCardWrapper>
      <Stack gap="12px" alignItems="flex-start">
        <Badge colorPalette="lightGray">{computedMeta.entityLabel}</Badge>

        <Stack gap="6px" width="100%">
          <Typography type="meta" lineHeight="normal">
            {title}
          </Typography>

          {description.map((detail) => (
            <Typography
              key={detail}
              fontSize="14px"
              fontWeight="400"
              color="gray"
              lineHeight="normal"
            >
              {detail}
            </Typography>
          ))}
        </Stack>

        <Badge
          bg={statusTokens.bg}
          color={statusTokens.color}
          borderRadius="12px"
        >
          {statusText}
        </Badge>
      </Stack>
    </WidgetCardWrapper>
  );
};

const buildDescription = (widget: Widget, subtitle?: string): string[] => {
  const details = new Set<string>();

  if (subtitle) {
    details.add(subtitle);
  }

  const payload = getWidgetPayload(widget);
  const description = getFirstString(payload, [
    'description',
    'summary',
    'details',
    'note',
  ]);

  if (description) {
    details.add(description);
  }

  return Array.from(details);
};
